import { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Table, TableHead, TableRow,
  TableCell, TableBody, Checkbox, FormControlLabel, Alert, CircularProgress, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getProductionOrder, launchProductionOrder, type ProductionOrderListItem, type LaunchOverride } from '../../api/productieOrdine'
import { getBom } from '../../api/productie'
import { getWarehouse } from '../../api/depozite'
import { errorMessage } from '../../api/client'

interface OverrideState { enabled: boolean; reason: string; zoneId: string; locationId: string; lotNumber: string; quantity: string }
const DEFAULT_OVERRIDE: Omit<OverrideState, 'quantity'> = { enabled: false, reason: '', zoneId: '', locationId: '', lotNumber: '' }

export default function LaunchOrderDialog({ target, onClose, onLaunched }: { target: ProductionOrderListItem | null; onClose: () => void; onLaunched: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [overrides, setOverrides] = useState<Record<string, Partial<OverrideState>>>({})
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)

  useEffect(() => { if (open) setPendingMessage(null) }, [open])

  const { data: order } = useQuery({ queryKey: ['productie', 'ordine', target?.id ?? null], queryFn: () => getProductionOrder(target!.id), enabled: open })
  const { data: bom } = useQuery({ queryKey: ['productie', 'retetar', order?.bomId ?? null], queryFn: () => getBom(order!.bomId), enabled: !!order })
  const { data: warehouseDetail } = useQuery({ queryKey: ['stocuri', 'depozit', order?.warehouseId ?? null], queryFn: () => getWarehouse(order!.warehouseId), enabled: !!order })

  const overrideFor = (articleId: string, required: number): OverrideState =>
    ({ ...DEFAULT_OVERRIDE, quantity: String(required), ...overrides[articleId] })

  const mutation = useMutation({
    mutationFn: () => {
      const requestedOverrides: LaunchOverride[] = (bom?.lines ?? [])
        .map((l) => ({ articleId: l.componentArticleId, required: l.quantityPerThousand * (order?.plannedQuantityThousands ?? 0) }))
        .map(({ articleId, required }) => ({ articleId, o: overrideFor(articleId, required) }))
        .filter(({ o }) => o.enabled)
        .map(({ articleId, o }) => ({
          componentArticleId: articleId, reason: o.reason,
          buckets: [{ zoneId: o.zoneId || null, locationId: o.locationId || null, lotNumber: o.lotNumber || null, quantity: Number(o.quantity) }],
        }))
      return launchProductionOrder(target!.id, { overrides: requestedOverrides.length > 0 ? requestedOverrides : null })
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['productie', 'ordine'] })
      if (r.pendingApproval) {
        setPendingMessage('Lansarea a fost trimisă la aprobare — ordinul va fi lansat automat după decizie.')
        onLaunched()
      } else {
        onLaunched()
        onClose()
      }
    },
  })

  const setOverride = (articleId: string, patch: Partial<OverrideState>) =>
    setOverrides((prev) => ({ ...prev, [articleId]: { ...prev[articleId], ...patch } }))

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Lansează {order?.number}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {pendingMessage ? (
            <Alert severity="info">{pendingMessage}</Alert>
          ) : (
          <>
          <Typography variant="body2" color="text.secondary">
            Alocarea implicită se face automat pe principiul FEFO (lotul cu expirarea cea mai apropiată întâi, loturile deja expirate fiind excluse).
            Bifați „suprascriere manuală" doar dacă trebuie ales explicit alt lot/zonă, cu motiv.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Componentă</TableCell><TableCell align="right">Necesar</TableCell><TableCell>Suprascriere manuală</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bom?.lines.map((l) => {
                const required = order ? l.quantityPerThousand * order.plannedQuantityThousands : 0
                const o = overrideFor(l.componentArticleId, required)
                const zone = warehouseDetail?.zones.find((z) => z.id === o.zoneId)
                return (
                  <TableRow key={l.id}>
                    <TableCell>{l.componentArticleCode} — {l.componentArticleDescription}</TableCell>
                    <TableCell align="right">{required.toFixed(3).replace('.', ',')}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={<Checkbox checked={o.enabled} onChange={(e) => setOverride(l.componentArticleId, { enabled: e.target.checked })} />}
                        label="Activează" />
                      {o.enabled && (
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <TextField size="small" label="Motiv (obligatoriu)" value={o.reason} onChange={(e) => setOverride(l.componentArticleId, { reason: e.target.value })} />
                          <Stack direction="row" spacing={1}>
                            <TextField size="small" select label="Zonă" sx={{ width: 140 }} value={o.zoneId}
                              onChange={(e) => setOverride(l.componentArticleId, { zoneId: e.target.value, locationId: '' })}>
                              <MenuItem value="">—</MenuItem>
                              {warehouseDetail?.zones.map((z) => <MenuItem key={z.id} value={z.id!}>{z.name}</MenuItem>)}
                            </TextField>
                            <TextField size="small" select label="Locație" sx={{ width: 140 }} value={o.locationId} disabled={!zone}
                              onChange={(e) => setOverride(l.componentArticleId, { locationId: e.target.value })}>
                              <MenuItem value="">—</MenuItem>
                              {zone?.locations.map((loc) => <MenuItem key={loc.id} value={loc.id!}>{loc.code}</MenuItem>)}
                            </TextField>
                            <TextField size="small" label="Lot" sx={{ width: 140 }} value={o.lotNumber} onChange={(e) => setOverride(l.componentArticleId, { lotNumber: e.target.value })} />
                            <TextField size="small" type="number" label="Cantitate" sx={{ width: 120 }} value={o.quantity} onChange={(e) => setOverride(l.componentArticleId, { quantity: e.target.value })} />
                          </Stack>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>{pendingMessage ? 'Închide' : 'Renunță'}</Button>
        {!pendingMessage && (
          <Button variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate()}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Lansează
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
